/**
 * dropOffReasons.ts — "what's holding you back?", the patient half of T-2.
 *
 * ⚠️ THE KEYS MIRROR clinic-service's `DROP_OFF_REASONS` AND MUST NOT DRIFT.
 * They are hard-coded rather than fetched, for the same reason the S-2 intent
 * vocabulary is: the client renders, the SERVER decides what is valid (R41) —
 * an unknown key is refused by the strict body, and a legacy value is nulled
 * on the way back out.
 *
 * `cost`, NOT `price` — `price` is a live GA4 ecommerce key on the dataLayer.
 *
 * TWO REASONS ARE A CLINICAL QUESTION, not a commercial one, and the page
 * answers them with a route to a clinician rather than a thank-you. A patient
 * worried about side effects must never be answered with a discount.
 */

export const DROP_OFF_REASON_OPTIONS = [
  { key: 'cost', label: 'The cost' },
  { key: 'comparing_providers', label: "I'm comparing providers" },
  { key: 'unsure_about_treatment', label: "I'm not sure this treatment is right for me" },
  { key: 'worried_about_side_effects', label: "I'm worried about side effects" },
  { key: 'want_to_speak_to_someone', label: 'I want to speak to someone first' },
  { key: 'not_ready', label: "I'm not ready yet" },
  { key: 'decided_against', label: "I've decided not to go ahead" },
  { key: 'other', label: 'Something else' },
] as const;

export type DropOffReasonKey = (typeof DROP_OFF_REASON_OPTIONS)[number]['key'];

/** Reasons answered with a route to a person rather than only a thank-you. */
export const PEOPLE_ROUTED_REASONS: readonly DropOffReasonKey[] = [
  'unsure_about_treatment',
  'worried_about_side_effects',
  'want_to_speak_to_someone',
];

/** The fragment key the plan page uses to hand this page its assessment id. */
export const DROP_OFF_HANDOFF_KEY = 'assessment';

/**
 * The page this link opens, WITH THE ID IN THE FRAGMENT, never the query.
 *
 * WHY A FRAGMENT: the page opens in a NEW TAB (see SecondThoughtsLink — pushing
 * a route from inside checkout strands the patient on Back), and a new tab has
 * no router state to carry the id in. A query string would put an identifiable
 * patient's assessment id into access logs, history and the Referer. A fragment
 * is never sent to a server — the same posture as the plan link's `#resume=`.
 * The page reads it once and scrubs it.
 */
export function dropOffPagePath(clinicSlug: string, assessmentId: string): string {
  return `/${encodeURIComponent(clinicSlug)}/not-ready#${DROP_OFF_HANDOFF_KEY}=${encodeURIComponent(assessmentId)}`;
}

/** The handed-over assessment id, or null when the page was opened without one. */
export function readDropOffAssessmentId(hash: string): string | null {
  const value = new URLSearchParams(hash.replace(/^#/, '')).get(DROP_OFF_HANDOFF_KEY)?.trim();
  return value ? value : null;
}
